// Port of SuiteModel/ProseMarkdown.swift: the constrained Markdown dialect prose fragments store. Swift reads it by
// Character (grapheme cluster), so this does too (`graphemes`, `hasPrefix`…), and trims by Foundation's scalar sets.
import type { Alignment, Block, BlockKind, FragmentKind, Inline, InkColor, InlineStyle, ListStyle, Prose } from './contract.ts';
import { plainText as fountainPlainText } from './fountain.ts';
import {
  charIsWhitespace, firstGraphemes, graphemes, hasPrefix, hasSuffix, indexOf, isCFAlphanumeric, isCFWhitespace, isCFWhitespaceOrNewline,
  scalars, splitLines, trimCFWhitespace,
} from './unicode.ts';

/** `InkColor.allCases`, in stored order (a style's colour bits are 1 + the index: only ever append). */
export const INK_COLORS: readonly InkColor[] = ['yellow', 'green', 'blue', 'pink', 'purple', 'orange', 'red', 'grey'];
export const ALIGNMENTS: readonly Alignment[] = ['center', 'right', 'justify'];

// MARK: InlineStyle

export const plainStyle = (): InlineStyle =>
  ({ italic: false, bold: false, smallCaps: false, underline: false, highlight: null, color: null });

export function sameStyle(a: InlineStyle, b: InlineStyle): boolean {
  return a.italic === b.italic && a.bold === b.bold && a.smallCaps === b.smallCaps && a.underline === b.underline &&
    a.highlight === b.highlight && a.color === b.color;
}

/** `InlineStyle.rawValue`: bit 0 italic, 1 bold, 2 small caps, 3 underline, 4–7 highlight, 8–11 colour. */
export function styleRawValue(s: InlineStyle): number {
  const ink = (c: InkColor | null) => (c ? INK_COLORS.indexOf(c) + 1 : 0);
  return (s.italic ? 1 : 0) | (s.bold ? 2 : 0) | (s.smallCaps ? 4 : 0) | (s.underline ? 8 : 0) | (ink(s.highlight) << 4) | (ink(s.color) << 8);
}

export function styleFromRawValue(raw: number): InlineStyle {
  const ink = (bits: number): InkColor | null => { const n = bits & 0xf; return n === 0 || n > INK_COLORS.length ? null : INK_COLORS[n - 1]; };
  return { italic: !!(raw & 1), bold: !!(raw & 2), smallCaps: !!(raw & 4), underline: !!(raw & 8), highlight: ink(raw >> 4), color: ink(raw >> 8) };
}

/** `spanOnly`: small caps, underline and the writer's colours, without emphasis. */
const spanOnly = (s: InlineStyle): InlineStyle => ({ ...s, italic: false, bold: false });
/** `emphasisOnly`. */
const emphasisOnly = (s: InlineStyle): InlineStyle => ({ ...plainStyle(), italic: s.italic, bold: s.bold });
/** `withoutMarks`: what compile sees. */
export const withoutMarks = (s: InlineStyle): InlineStyle => ({ ...s, highlight: null, color: null });

/** `spanClasses`: `{.sc .u .hl-yellow .c-red}`, or null when there's no span. */
export function spanClasses(s: InlineStyle): string | null {
  const names: string[] = [];
  if (s.smallCaps) names.push('.sc');
  if (s.underline) names.push('.u');
  if (s.highlight) names.push('.hl-' + s.highlight);
  if (s.color) names.push('.c-' + s.color);
  return names.length === 0 ? null : '{' + names.join(' ') + '}';
}

/** `InlineStyle.span(classes:)`: the styles written by `{…}` (braces included), or null when any class is unknown. */
export function spanStyle(classes: string): InlineStyle | null {
  if (!hasPrefix(classes, '{') || !hasSuffix(classes, '}')) return null;
  // `dropFirst().dropLast().split(separator: " ")`: Characters, empty pieces omitted.
  const inner = graphemes(classes).slice(1, -1);
  const names: string[] = [];
  let current = '';
  for (const c of inner) {
    if (c === ' ') { if (current) names.push(current); current = ''; } else current += c;
  }
  if (current) names.push(current);
  if (names.length === 0) return null;
  const style = plainStyle();
  for (const name of names) {
    if (name === '.sc') style.smallCaps = true;
    else if (name === '.u') style.underline = true;
    else if (hasPrefix(name, '.hl-') && (INK_COLORS as string[]).includes(name.slice(4))) style.highlight = name.slice(4) as InkColor;
    else if (hasPrefix(name, '.c-') && (INK_COLORS as string[]).includes(name.slice(3))) style.color = name.slice(3) as InkColor;
    else return null;
  }
  return style;
}

// MARK: ListStyle

export function isNumbered(style: ListStyle): boolean {
  return style !== 'bullet' && style !== 'circle' && style !== 'dash';
}

/** `ListStyle.letters`: a…z, then aa, ab… */
export function letters(n: number): string {
  let out = '';
  while (n > 0) {
    n -= 1;
    out = String.fromCharCode(97 + (n % 26)) + out;
    n = Math.floor(n / 26);
  }
  return out;
}

/** `ListStyle.roman` (lower case; 4000 and above as digits). */
export function roman(n: number): string {
  if (n >= 4000) return String(n);
  const table: [number, string][] = [[1000, 'm'], [900, 'cm'], [500, 'd'], [400, 'cd'], [100, 'c'], [90, 'xc'],
    [50, 'l'], [40, 'xl'], [10, 'x'], [9, 'ix'], [5, 'v'], [4, 'iv'], [1, 'i']];
  let out = '';
  for (const [value, numeral] of table) while (n >= value) { out += numeral; n -= value; }
  return out;
}

function romanValue(s: string): number | null {
  const values: Record<string, number> = { i: 1, v: 5, x: 10, l: 50, c: 100, d: 500, m: 1000 };
  const lower = s.toLowerCase();
  let total = 0, previous = 0;
  for (const c of [...lower].reverse()) {
    const v = values[c];
    if (v === undefined) return null;
    total += v < previous ? -v : v;
    previous = Math.max(previous, v);
  }
  return total > 0 && roman(total) === lower ? total : null;
}

/** `ListStyle.marker`: the marker for item `number` (1-based), without its tab. */
export function marker(style: ListStyle, number: number): string {
  const n = Math.max(1, number);
  switch (style) {
    case 'bullet': return '•';
    case 'circle': return '◦';
    case 'dash': return '–';
    case 'decimal': return `${n}.`;
    case 'decimalParen': return `${n})`;
    case 'lowerAlpha': return letters(n) + '.';
    case 'upperAlpha': return letters(n).toUpperCase() + '.';
    case 'lowerRoman': return roman(n) + '.';
    case 'upperRoman': return roman(n).toUpperCase() + '.';
  }
}

export interface ListMarker { style: ListStyle; number: number; /** In Characters, the tab included. */ length: number }

const isAsciiDigit = (c: string) => c.length === 1 && c >= '0' && c <= '9';
const isAsciiUpper = (c: string) => c.length === 1 && c >= 'A' && c <= 'Z';
const isAsciiLower = (c: string) => c.length === 1 && c >= 'a' && c <= 'z';

/** `ListStyle.parse(line:continuing:)`: a list line's marker. `continuing` is the style of the item before it in the
 *  same block, which settles whether "i." is a letter or a numeral. */
export function parseListMarker(line: string | string[], continuing: ListStyle | null = null): ListMarker | null {
  const chars = typeof line === 'string' ? firstGraphemes(line, 13) : line;
  const tab = chars.slice(0, 12).indexOf('\t');
  if (tab < 0) return null;
  const body0 = chars.slice(0, tab);
  const length = tab + 1;
  const markerText = body0.join('');
  if (markerText === '•') return { style: 'bullet', number: 1, length };
  if (markerText === '◦') return { style: 'circle', number: 1, length };
  if (markerText === '–') return { style: 'dash', number: 1, length };
  const last = body0[body0.length - 1];
  if (last !== '.' && last !== ')') return null;
  const body = body0.slice(0, -1);
  if (body.length === 0) return null;
  if (body.every(isAsciiDigit) && body.length <= 9) {
    return { style: last === ')' ? 'decimalParen' : 'decimal', number: parseInt(body.join(''), 10), length };
  }
  if (last !== '.' || !body.every((c) => isAsciiUpper(c) || isAsciiLower(c))) return null;
  const upper = body.every(isAsciiUpper);
  if (!upper && !body.every(isAsciiLower)) return null;
  const alpha: ListStyle = upper ? 'upperAlpha' : 'lowerAlpha';
  const romanStyle: ListStyle = upper ? 'upperRoman' : 'lowerRoman';
  const text = body.join('');
  if (body.length === 1) {
    const n = text.toLowerCase().charCodeAt(0) - 96;
    // A single letter is a letter, unless it's "i" opening a list (or following roman items).
    if (continuing === alpha) return { style: alpha, number: n, length };
    const r = romanValue(text);
    if (continuing === romanStyle && r !== null) return { style: romanStyle, number: r, length };
    if (text.toLowerCase() === 'i') return { style: romanStyle, number: 1, length };
    return { style: alpha, number: n, length };
  }
  const r = romanValue(text);
  return r !== null ? { style: romanStyle, number: r, length } : null;
}

// MARK: Blocks

export function listStyleOf(block: Block): ListStyle | null {
  return block.kind.type === 'listItem' ? block.kind.style : null;
}

/** `Block.startsList(after:)`: whether this list item begins a list rather than continuing `previous`. */
export function startsList(block: Block, previous: Block | null): boolean {
  if (block.kind.type !== 'listItem') return false;
  if (!previous || previous.kind.type !== 'listItem') return true;
  const { style, number } = block.kind;
  return previous.kind.style !== style || (isNumbered(style) && number !== previous.kind.number + 1);
}

/** `Block.plainText` (a link gives its words, never its address). */
export function blockPlainText(block: Block): string {
  let out = '';
  for (const inline of block.inlines) {
    if (inline.type === 'text') out += inline.text;
    else if (inline.type === 'lineBreak') out += '\n';
  }
  return out;
}

const block = (kind: BlockKind, inlines: Inline[] = [], alignment: Alignment | null = null): Block => ({ kind, inlines, alignment });

// MARK: Parsing

const LINE_BREAK = '\u2028';

/** `ProseMarkdown.parse`. */
export function parse(source: string): Block[] {
  const blocks: Block[] = [];
  const lines = splitLines(source.replaceAll('\r\n', '\n'));
  let i = 0;
  let paragraph: string[] = [];
  let quote: string[] = [];

  const flush = () => {
    if (paragraph.length > 0) {
      blocks.push(...parseParagraph(paragraph));
      paragraph = [];
    }
    if (quote.length > 0) {
      blocks.push(block({ type: 'blockQuote' }, parseInlines(join(quote))));
      quote = [];
    }
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = trimCFWhitespace(line);
    let fence: ParagraphFence | null;
    if (trimmed === '') {
      flush();
    } else if (trimmed === '***' || trimmed === '* * *') {
      flush();
      blocks.push(block({ type: 'sceneBreak' }));
    } else if (trimmed === '::: epigraph') {
      flush();
      const body: string[] = [];
      i += 1;
      while (i < lines.length && trimCFWhitespace(lines[i]) !== ':::') {
        body.push(lines[i]);
        i += 1;
      }
      blocks.push(block({ type: 'epigraph' }, parseInlines(join(body.filter((l) => l !== '')))));
    } else if ((fence = paragraphFence(trimmed))) {
      flush();
      // Each paragraph inside takes the fence's kind and alignment (a verse fence's paragraphs are stanzas).
      let body: string[] = [];
      i += 1;
      while (i < lines.length && trimCFWhitespace(lines[i]) !== ':::') {
        if (trimCFWhitespace(lines[i]) === '') {
          if (body.length > 0) blocks.push(block(fence.kind, parseInlines(join(body)), fence.alignment));
          body = [];
        } else {
          body.push(lines[i]);
        }
        i += 1;
      }
      if (body.length > 0) blocks.push(block(fence.kind, parseInlines(join(body)), fence.alignment));
    } else if (hasPrefix(line, '>')) {
      if (paragraph.length > 0) flush();
      quote.push(line.slice(hasPrefix(line, '> ') ? 2 : 1));
    } else {
      if (quote.length > 0) flush();
      paragraph.push(line);
    }
    i += 1;
  }
  flush();
  return blocks;
}

export interface ParagraphFence { kind: BlockKind; alignment: Alignment | null }

/** `ProseMarkdown.paragraphFence`: the fence a trimmed line opens whose paragraphs each become a block — `::: center|right|
 *  justify` (paragraphs set so) or `::: verse` with an optional alignment after it (stanzas). Null for anything else
 *  (`::: epigraph` is its own block; an unknown fence is text). */
export function paragraphFence(trimmed: string): ParagraphFence | null {
  if (!trimmed.startsWith('::: ')) return null;
  const words = trimmed.slice(4).split(' ').filter((w) => w !== '');
  if (words[0] === 'verse') {
    if (words.length > 2) return null;
    if (words.length === 2) {
      const a = ALIGNMENTS.find((x) => x === words[1]);
      if (!a || a === 'justify') return null;
      return { kind: { type: 'verse' }, alignment: a };
    }
    return { kind: { type: 'verse' }, alignment: null };
  }
  if (words.length !== 1) return null;
  const a = ALIGNMENTS.find((x) => x === words[0]);
  return a ? { kind: { type: 'paragraph' }, alignment: a } : null;
}

/** `ProseMarkdown.verseFence`: the fence line that opens a verse block set `alignment` (null: flush left). */
export function verseFence(alignment: Alignment | null = null): string {
  return alignment ? `::: verse ${alignment}` : '::: verse';
}

// MARK: Verse

/** A verse block's indent step, in ems of the body size, and the most steps a line takes (more read as six). */
export const VERSE_INDENT_EM = 2;
export const MAX_VERSE_INDENT = 6;

/** One line of a stanza: its indent in steps and its inlines without the tabs that set it. */
export interface VerseLine { indent: number; inlines: Inline[] }

/** `ProseMarkdown.verseLines(of:)`: a stanza's lines, split at its hard breaks, each line's leading tabs counted as indent
 *  steps (capped at `MAX_VERSE_INDENT`) and taken out of its text. A line with no text is kept. */
export function verseLines(block: Block): VerseLine[] {
  const lines: Inline[][] = [[]];
  for (const inline of block.inlines) {
    if (inline.type === 'lineBreak') lines.push([]);
    else lines[lines.length - 1].push(inline);
  }
  return lines.map((inlines) => {
    let indent = 0;
    const rest: Inline[] = [];
    let counting = true;
    for (const inline of inlines) {
      if (counting && inline.type === 'text') {
        let tabs = 0;
        while (tabs < inline.text.length && inline.text[tabs] === '\t') tabs++;
        indent += tabs;
        if (tabs < inline.text.length) {
          counting = false;
          rest.push({ type: 'text', text: inline.text.slice(tabs), style: inline.style });
        }
        continue;
      }
      counting = false;
      rest.push(inline);
    }
    return { indent: Math.min(indent, MAX_VERSE_INDENT), inlines: rest };
  });
}

function parseParagraph(lines: string[]): Block[] {
  const first = lines[0];
  if (hasPrefix(first, '[^')) {
    const close = indexOf(first, ']:');
    if (close >= 0) {
      const id = first.slice(2, close);
      const rest = [...lines];
      const after = graphemes(first.slice(close + 2));
      let spaces = 0;
      while (spaces < after.length && after[spaces] === ' ') spaces++;
      rest[0] = after.slice(spaces).join('');
      return [block({ type: 'footnote', id }, parseInlines(join(rest)))];
    }
  }
  if (parseListMarker(first) !== null) return parseList(lines);
  return [block({ type: 'paragraph' }, parseInlines(join(lines)))];
}

/** A block whose first line is a list item: each marked line starts an item, other lines continue the one before. */
function parseList(lines: string[]): Block[] {
  const items: Block[] = [];
  let style: ListStyle | null = null;
  let number = 0;
  let body: string[] = [];
  const flush = () => {
    if (style && body.length > 0) items.push(block({ type: 'listItem', style, number }, parseInlines(join(body))));
    body = [];
  };
  for (const line of lines) {
    // A line after a hard break is the item's, even if it looks like a marker.
    const last = body[body.length - 1];
    const continues = last !== undefined && hasSuffix(last, '\\') && !hasSuffix(last, '\\\\');
    const m: ListMarker | null = continues ? null : parseListMarker(line, style);
    if (m) {
      flush();
      number = m.style === style ? number + 1 : m.number;
      style = m.style;
      body = [graphemes(line).slice(m.length).join('')];
    } else {
      body.push(line);
    }
  }
  flush();
  return items;
}

/** Joins source lines: a trailing backslash is a hard break, otherwise lines are soft-wrapped. */
function join(lines: string[]): string {
  let out = '';
  lines.forEach((line, n) => {
    if (n > 0) out += out.endsWith(LINE_BREAK) ? '' : ' ';
    if (hasSuffix(line, '\\') && !hasSuffix(line, '\\\\') && n < lines.length - 1) out += line.slice(0, -1) + LINE_BREAK;
    else out += line;
  });
  return out;
}

/** `parseInlines`: emphasis runs, escapes, footnote references, links and bracketed spans. */
export function parseInlines(source: string): Inline[] {
  const result: Inline[] = [];
  let style = plainStyle();
  let buffer = '';
  const chars = graphemes(source);
  let i = 0;
  // The open span's close: where its `]` is and how long `]{…}` runs (a link's `](address){…}` too).
  let spanClose: { at: number; length: number } | null = null;
  let inLink = false;

  const emit = () => {
    if (buffer !== '') {
      result.push({ type: 'text', text: buffer, style: { ...style } });
      buffer = '';
    }
  };

  while (i < chars.length) {
    const c = chars[i];
    if (c === '\\' && i + 1 < chars.length) {
      buffer += chars[i + 1];
      i += 2;
      continue;
    }
    if (c === LINE_BREAK) {
      emit();
      result.push({ type: 'lineBreak' });
    } else if (c === '*') {
      let n = 0;
      while (i + n < chars.length && chars[i + n] === '*') n++;
      emit();
      if (n % 2 === 1) style = { ...style, italic: !style.italic };
      if (n >= 2) style = { ...style, bold: !style.bold };
      i += n;
      continue;
    } else if (c === '[' && i + 1 < chars.length && chars[i + 1] === '^') {
      const close = chars.indexOf(']', i + 2);
      if (close >= 0) {
        emit();
        result.push({ type: 'footnoteReference', id: chars.slice(i + 2, close).join('') });
        i = close + 1;
        continue;
      }
      buffer += c;
    } else if (c === '[' && spanClose === null) {
      const link = linkClose(chars, i + 1);
      const close = link ? null : spanEnd(chars, i + 1);
      if (link) {
        emit();
        result.push({ type: 'linkStart', address: link.address });
        style = { ...link.style, italic: style.italic, bold: style.bold };
        spanClose = { at: link.at, length: link.end - link.at };
        inLink = true;
      } else if (close) {
        emit();
        style = { ...close.style, italic: style.italic, bold: style.bold };
        spanClose = { at: close.at, length: close.length };
      } else {
        buffer += c;
      }
    } else if (c === ']' && spanClose?.at === i) {
      emit();
      if (inLink) result.push({ type: 'linkEnd' });
      inLink = false;
      style = emphasisOnly(style);
      i += spanClose.length;
      spanClose = null;
      continue;
    } else {
      buffer += c;
    }
    i += 1;
  }
  emit();
  return result;
}

/** The close of a span opened just before `start`: the first unescaped `]` (no `[` before it) followed by classes
 *  the dialect knows. Null when there's none, and the `[` is text. */
function spanEnd(chars: string[], start: number): { at: number; length: number; style: InlineStyle } | null {
  let i = start;
  while (i < chars.length) {
    if (chars[i] === '\\') { i += 2; continue; }
    if (chars[i] === '[') return null;
    if (chars[i] === ']') {
      if (i + 1 >= chars.length || chars[i + 1] !== '{') return null;
      const brace = chars.indexOf('}', i + 1);
      if (brace < 0) return null;
      const style = spanStyle(chars.slice(i + 1, brace + 1).join(''));
      if (!style) return null;
      return { at: i, length: brace - i + 1, style };
    }
    i += 1;
  }
  return null;
}

// MARK: Links (ProseMarkdown+Links.swift)

// `[words](address)`, CommonMark's form, when the address starts with a scheme the dialect knows. Without one the
// brackets are text, so nothing a writer typed before links existed (`[see below](later)`, a merge tag before a
// parenthesis) becomes a link. A class block after the link styles its words: `[words](address){.u .hl-yellow}`. A link
// stays on one line; its words hold emphasis, not spans, footnote references or another link, as a span's do.

/** `linkSchemes`: the schemes a link's address may start with; `fragment:` names a document in the same project. */
export const LINK_SCHEMES: readonly string[] = ['https://', 'http://', 'mailto:', 'fragment:'];

/** `isLinkAddress`: a known scheme and something after it. */
export function isLinkAddress(address: string): boolean {
  const lower = address.toLowerCase();
  return LINK_SCHEMES.some((scheme) => hasPrefix(lower, scheme) && graphemes(lower).length > scheme.length);
}

const CONTROL = /^[\p{Cc}\p{Cf}]$/u;

/** `linkAddress`: an address as a link writes it — spaces, parentheses, angle brackets, backslashes, quotes and control
 *  characters percent-encoded, so the address ends at its own `)` and survives anything that reads it as words. */
export function linkAddress(address: string): string {
  const cps = [...scalars(address)];
  let from = 0, to = cps.length;
  while (from < to && isCFWhitespaceOrNewline(cps[from])) from++;
  while (to > from && isCFWhitespaceOrNewline(cps[to - 1])) to--;
  const hex = (n: number) => '%' + n.toString(16).toUpperCase().padStart(2, '0');
  let out = '';
  for (const cp of cps.slice(from, to)) {
    const c = String.fromCodePoint(cp);
    if (' ()<>\\"[]`{}'.includes(c)) out += hex(cp);
    else if (isCFWhitespaceOrNewline(cp) || CONTROL.test(c)) for (const byte of new TextEncoder().encode(c)) out += hex(byte);
    else out += c;
  }
  return out;
}

/** `linkClose`: the close of a link opened just before `start` in `chars` — its `]` (the first unescaped one, no `[`
 *  before it, on the same line), the address in the `(…)` straight after it (balanced parentheses, no spaces, an escape
 *  taken as the character it escapes), and an optional class block. Null when it isn't a link. */
export function linkClose(chars: string[], start: number): { at: number; end: number; address: string; style: InlineStyle } | null {
  let i = start;
  while (i < chars.length) {
    const c = chars[i];
    if (c === '\\') { i += 2; continue; }
    if (c === '[' || c === '\n' || c === LINE_BREAK) return null;
    if (c === ']') {
      if (i + 1 >= chars.length || chars[i + 1] !== '(') return null;
      let j = i + 2;
      let depth = 0;
      let address = '';
      while (j < chars.length) {
        const d = chars[j];
        if (d === '\\' && j + 1 < chars.length) { address += chars[j + 1]; j += 2; continue; }
        if (charIsWhitespace(d) || d === LINE_BREAK) return null;
        if (d === '(') depth += 1;
        if (d === ')') {
          if (depth === 0) break;
          depth -= 1;
        }
        address += d;
        j += 1;
      }
      if (j >= chars.length || !isLinkAddress(address)) return null;
      let end = j + 1;
      let style = plainStyle();
      if (end < chars.length && chars[end] === '{') {
        const brace = chars.indexOf('}', end);
        const classes = brace >= 0 ? spanStyle(chars.slice(end, brace + 1).join('')) : null;
        if (classes) {
          style = classes;
          end = brace + 1;
        }
      }
      return { at: i, end, address, style };
    }
    i += 1;
  }
  return null;
}

/** `LinkRange`: one link in source text, in UTF-16 offsets [from, to): all of it, its words, and its syntax (the `[`,
 *  and the `](address){…}` after the words). */
export interface LinkRange {
  range: [number, number];
  words: [number, number];
  open: [number, number];
  close: [number, number];
  address: string;
}

/** `ProseMarkdown.links(in:)`: every link in `text` (stored Markdown), as `parse` reads them, for code that works on
 *  the source: the checks leave addresses alone, Find matches words. Footnote references and escapes are passed over
 *  as the parser does. */
export function links(text: string): LinkRange[] {
  if (!text.includes('](')) return [];
  const length = text.length;
  const at = (k: number) => text.charCodeAt(k);
  const BACKSLASH = 0x5c, OPEN = 0x5b, CLOSE = 0x5d, PAREN = 0x28, CLOSE_PAREN = 0x29, CARET = 0x5e;
  const BRACE = 0x7b, CLOSE_BRACE = 0x7d, NEWLINE = 0x0a;
  const found: LinkRange[] = [];
  let i = 0;
  // A span open: its close, so a `[` inside it isn't read (spans and links don't nest).
  let spanCloseAt: number | null = null;
  while (i < length) {
    const c = at(i);
    if (c === NEWLINE) spanCloseAt = null;
    if (c === BACKSLASH) { i += 2; continue; }
    if (spanCloseAt !== null && i === spanCloseAt) spanCloseAt = null;
    if (c === OPEN && i + 1 < length && at(i + 1) === CARET) {
      let j = i + 2;
      while (j < length && at(j) !== CLOSE && at(j) !== NEWLINE) j++;
      if (j < length && at(j) === CLOSE) { i = j + 1; continue; }
    }
    if (c !== OPEN || spanCloseAt !== null) { i += 1; continue; }
    // The `]` this `[` would close.
    let j: number = i + 1;
    let bracket: number | null = null;
    while (j < length) {
      const d = at(j);
      if (d === BACKSLASH) { j += 2; continue; }
      if (d === OPEN || d === NEWLINE) break;
      if (d === CLOSE) { bracket = j; break; }
      j += 1;
    }
    if (bracket === null || bracket + 1 >= length) { i += 1; continue; }
    const b: number = bracket;
    const next = at(b + 1);
    if (next === BRACE) {
      // A span: its classes decide, as `spanEnd` does.
      let k = b + 2;
      while (k < length && at(k) !== CLOSE_BRACE && at(k) !== NEWLINE) k++;
      if (k < length && at(k) === CLOSE_BRACE && spanStyle(text.slice(b + 1, k + 1)) !== null) spanCloseAt = b;
      i += 1;
      continue;
    }
    if (next !== PAREN) { i += 1; continue; }
    let k = b + 2;
    let depth = 0;
    let address = '';
    let ok = false;
    while (k < length) {
      const d = at(k);
      if (d === BACKSLASH && k + 1 < length) {
        address += text[k + 1];
        k += 2;
        continue;
      }
      if (d === 32 || d === 9 || d === NEWLINE || d === 0x2028) break;
      if (d === PAREN) depth += 1;
      if (d === CLOSE_PAREN) {
        if (depth === 0) { ok = true; break; }
        depth -= 1;
      }
      address += text[k];
      k += 1;
    }
    if (!ok || !isLinkAddress(address)) { i += 1; continue; }
    let end = k + 1;
    if (end < length && at(end) === BRACE) {
      let m = end + 1;
      while (m < length && at(m) !== CLOSE_BRACE && at(m) !== NEWLINE) m++;
      if (m < length && at(m) === CLOSE_BRACE && spanStyle(text.slice(end, m + 1)) !== null) end = m + 1;
    }
    found.push({ range: [i, end], words: [i + 1, b], open: [i, i + 1], close: [b, end], address });
    i = end;
  }
  return found;
}

/** `linkScalarRanges`: every link in `scalars` (stored Markdown, one string per scalar) in scalar offsets — all of it,
 *  and its syntax (`[` and `](address){…}`) — for code that works on scalars (QuoteMarks, Clean Up). */
export function linkScalarRanges(chars: readonly string[]): { whole: [number, number]; syntax: [number, number][] }[] {
  const found = links(chars.join(''));
  if (found.length === 0) return [];
  // UTF-16 offset → scalar index.
  const scalarAt = new Map<number, number>();
  let utf16 = 0;
  chars.forEach((c, n) => { scalarAt.set(utf16, n); utf16 += c.length; });
  scalarAt.set(utf16, chars.length);
  const range = (r: [number, number]): [number, number] => [scalarAt.get(r[0]) ?? 0, scalarAt.get(r[1]) ?? chars.length];
  return found.map((l) => ({ whole: range(l.range), syntax: [range(l.open), range(l.close)] }));
}

/** `linkSyntaxRanges(in:)`: the UTF-16 ranges [from, to) of every link's syntax (its `[` and `](address){…}`): what
 *  checks working on the source must leave alone and what a reader never sees. */
export function linkSyntaxRanges(text: string): [number, number][] {
  return links(text).flatMap((l) => [l.open, l.close]);
}

/** `removingLinks`: the text with every link's syntax taken out and its words left, for comparing text from before
 *  links existed (an import base, a hash) with text that has them. */
export function removingLinks(text: string): string {
  const found = links(text);
  if (found.length === 0) return text;
  let out = text;
  for (const link of [...found].reverse()) {
    out = out.slice(0, link.close[0]) + out.slice(link.close[1]);
    out = out.slice(0, link.open[0]) + out.slice(link.open[1]);
  }
  return out;
}

/** `ProseMarkdown.Linked`: the result of `linking` — the text, which links went in, and where a position (Unicode
 *  scalars) in the original text is now. */
export class Linked {
  constructor(
    readonly text: string,
    readonly applied: boolean[],
    /** Insertions in the original's scalars: where, and how many scalars went in. */
    readonly insertions: { at: number; length: number; isClose: boolean }[],
    /** The same insertions as text, in an order that can be spliced one after another into the original (each at or
     *  before the last, in the original's scalars): only the brackets go in, so a document two devices link at once
     *  ends with two whole links rather than their characters interleaved. */
    readonly edits: { at: number; value: string }[] = [],
  ) {}

  /** Where scalar position `p` of the original text is in `text`. A range's start just at a link's start stays before
   *  its `[`, and one just after its words goes after its `](address)`; a range's end at either stays inside. */
  position(p: number, isEnd: boolean): number {
    let shift = 0;
    for (const insertion of this.insertions) {
      if (insertion.isClose) {
        if (p > insertion.at || (p === insertion.at && !isEnd)) shift += insertion.length;
      } else if (p > insertion.at) {
        shift += insertion.length;
      }
    }
    return p + shift;
  }

  /** `range(_:)`: a scalar range [from, to) of the original, moved. */
  range(r: [number, number]): [number, number] {
    const lower = this.position(r[0], false);
    return [lower, Math.max(lower, this.position(r[1], true))];
  }
}

/** `linking(_:links:)`: `text` with each of `candidates` (a scalar range [from, to) of its words and an address) made a
 *  link — `[` before the words and `](address)` after. A link goes in only if the text then reads as exactly one more
 *  link and the same words (so never across a line, inside a span or another link, or round a footnote reference); the
 *  rest are reported as not applied, for the caller to keep as it did before links (a margin comment). */
export function linking(text: string, candidates: readonly { range: [number, number]; address: string }[]): Linked {
  let chars = Array.from(text);
  const count = chars.length;
  const applied = candidates.map(() => false);
  const insertions: { at: number; length: number; isClose: boolean }[] = [];
  const words = plainText(text);
  let linkCount = links(text).length;
  const taken: [number, number][] = [];
  const edits: { at: number; value: string }[] = [];
  // Last first, so each insertion leaves the positions before it as they were.
  const order = candidates.map((_, i) => i).sort((a, b) => candidates[b].range[0] - candidates[a].range[0]);
  for (const i of order) {
    const [lower, upper] = candidates[i].range;
    if (!(lower >= 0 && lower < upper && upper <= count && isLinkAddress(candidates[i].address))) continue;
    if (taken.some(([tl, tu]) => (tl < upper && lower < tu) || tl < upper)) continue;
    const close = Array.from('](' + candidates[i].address + ')');
    const candidate = [...chars.slice(0, lower), '[', ...chars.slice(lower, upper), ...close, ...chars.slice(upper)];
    const attempt = candidate.join('');
    if (plainText(attempt) !== words || links(attempt).length !== linkCount + 1) continue;
    chars = candidate;
    linkCount += 1;
    applied[i] = true;
    taken.push([lower, upper]);
    insertions.push({ at: lower, length: 1, isClose: false });
    insertions.push({ at: upper, length: close.length, isClose: true });
    edits.push({ at: upper, value: '](' + candidates[i].address + ')' }, { at: lower, value: '[' });
  }
  return new Linked(chars.join(''), applied, insertions, edits);
}

/** `ProseMarkdown.markdown(_:before:)`: the Markdown before `offset` (Unicode scalars), cut so it reads as the text
 *  before that point does: a span or link open there is closed with its own `]{…}` or `](address)`, and a footnote
 *  reference or escape cut short is left out. */
export function markdownBefore(source: string, offset: number): string {
  const scalars = Array.from(source);
  const cut = Math.max(0, Math.min(offset, scalars.length));
  const headChars = graphemes(scalars.slice(0, cut).join(''));
  const lineStart = headChars.lastIndexOf('\n') + 1;
  // The cut's line, both sides, scanned as `parseInlines` reads it.
  const before = headChars.slice(lineStart);
  const rest = graphemes(scalars.slice(cut).join(''));
  const lineEnd = rest.indexOf('\n');
  const chars = before.concat(lineEnd < 0 ? rest : rest.slice(0, lineEnd));
  const end = before.length;
  let head = headChars.join('');
  let open: { at: number; length: number } | null = null;
  let i = 0;
  while (i < end) {
    const c = chars[i];
    if (c === '\\') {
      // An escape cut short is left out (a span or link open before it is still closed, as the Swift does).
      if (i + 1 === end) { head = headChars.slice(0, -1).join(''); break; }
      i += 2;
      continue;
    }
    if (c === '[' && i + 1 < chars.length && chars[i + 1] === '^') {
      const close = chars.indexOf(']', i + 2);
      if (close >= 0) {
        if (close >= end) return headChars.slice(0, lineStart + i).join('');
        i = close + 1;
        continue;
      }
    }
    const link: ReturnType<typeof linkClose> = c === '[' && open === null ? linkClose(chars, i + 1) : null;
    const close: { at: number; length: number } | null = link ? { at: link.at, length: link.end - link.at }
      : c === '[' && open === null ? spanEnd(chars, i + 1) : null;
    if (close) {
      open = { at: close.at, length: close.length };
    } else if (open && i === open.at) {
      // Cut inside the close itself: take the rest of it.
      if (open.at + open.length > end) return head + chars.slice(end, open.at + open.length).join('');
      i += open.length;
      open = null;
      continue;
    }
    i += 1;
  }
  return open ? head + chars.slice(open.at, open.at + open.length).join('') : head;
}

// MARK: Serialising

/** `ProseMarkdown.serialize`: blocks back to the dialect. */
export function serialize(blocks: Block[]): string {
  let out = '';
  blocks.forEach((b, n) => {
    if (n > 0) {
      // A list's items are one block, a line each.
      const continues = listStyleOf(b) !== null && !startsList(b, blocks[n - 1]);
      out += continues ? '\n' : '\n\n';
    }
    out += serializeBlock(b);
  });
  return out;
}

function serializeBlock(b: Block): string {
  switch (b.kind.type) {
    case 'sceneBreak': return '***';
    case 'paragraph': {
      const text = escapeLeading(serializeInlines(b.inlines));
      return b.alignment ? `::: ${b.alignment}\n${text}\n:::` : text;
    }
    case 'listItem': return marker(b.kind.style, b.kind.number) + '\t' + serializeInlines(b.inlines);
    case 'blockQuote': return serializeInlines(b.inlines).split('\n').map((l) => '> ' + l).join('\n');
    case 'epigraph': return '::: epigraph\n' + escapeLeading(serializeInlines(b.inlines)) + '\n:::';
    case 'verse': return verseFence(b.alignment) + '\n' + escapeLeading(serializeInlines(b.inlines)) + '\n:::';
    case 'footnote': return `[^${b.kind.id}]: ` + serializeInlines(b.inlines);
  }
}

const isTrailingBreak = (inline: Inline) =>
  inline.type === 'lineBreak' || inline.type === 'linkEnd' ||
  (inline.type === 'text' && graphemes(inline.text).every((c) => /^\p{White_Space}/u.test(c)));

/** `serializeInlines`: a line after a hard break is escaped like a paragraph's first, and hard breaks at the very end,
 *  which the dialect can't hold, are dropped. */
export function serializeInlines(input: Inline[]): string {
  let inlines = input;
  let lastKept = -1;
  inlines.forEach((x, i) => { if (!isTrailingBreak(x)) lastKept = i; });
  const keep = lastKept + 1;
  if (inlines.slice(keep).some((x) => x.type === 'lineBreak')) inlines = inlines.slice(0, keep);
  const lines: string[] = [];
  let out = '';
  let current = plainStyle();
  // Whitespace is held back so closing markers land before it and opening markers after it.
  let pendingSpace = '';
  // The open link: its address and the classes its words take (from its first text). Inside it, span styles are the
  // link's classes, never a `[` of their own.
  let link: { address: string; classes: string | null; hasText: boolean } | null = null;
  // A link cut by a line break opens again on the next line.
  let reopen: string | null = null;

  const toggles = (s: InlineStyle) => '*'.repeat((s.italic ? 1 : 0) + (s.bold ? 2 : 0));
  const subtract = (a: InlineStyle, b: InlineStyle): InlineStyle => ({ ...plainStyle(), italic: a.italic && !b.italic, bold: a.bold && !b.bold });

  const transition = (next: InlineStyle) => {
    const spanChanges = link === null && !sameStyle(spanOnly(current), spanOnly(next));
    const classes = spanClasses(current);
    if (spanChanges && classes) out += ']' + classes;
    out += toggles(subtract(emphasisOnly(current), emphasisOnly(next)));
    out += pendingSpace;
    pendingSpace = '';
    out += toggles(subtract(emphasisOnly(next), emphasisOnly(current)));
    if (spanChanges && spanClasses(next) !== null) out += '[';
    current = link === null ? next : { ...spanOnly(current), italic: next.italic, bold: next.bold };
  };

  const openLink = (address: string) => {
    transition(emphasisOnly(current));
    out += '[';
    link = { address, classes: null, hasText: false };
  };

  /** Closes the open link, with the emphasis that doesn't go on past it closed inside it. */
  const closeLink = (next: InlineStyle) => {
    if (link === null) return;
    const closes = subtract(emphasisOnly(current), emphasisOnly(next));
    out += toggles(closes);
    current = { ...current, italic: current.italic && !closes.italic, bold: current.bold && !closes.bold };
    out += '](' + link.address + ')' + (link.classes ?? '');
    current = emphasisOnly(current);
    link = null;
  };

  /** The style of the next text from `index`: what follows a link's end. */
  const styleAfter = (index: number): InlineStyle => {
    for (const inline of inlines.slice(index)) {
      if (inline.type === 'text' && !graphemes(inline.text).every((c) => c === ' ')) return inline.style;
      if (inline.type === 'lineBreak' || inline.type === 'footnoteReference') return plainStyle();
    }
    return plainStyle();
  };

  inlines.forEach((inline, index) => {
    switch (inline.type) {
      case 'text': {
        const chars = graphemes(inline.text);
        let lead = 0;
        while (lead < chars.length && (chars[lead] === ' ' || chars[lead] === '\t')) lead++;
        if (lead === chars.length) { pendingSpace += inline.text; return; }
        let trail = 0;
        while (trail < chars.length && chars[chars.length - 1 - trail] === ' ') trail++;
        pendingSpace += chars.slice(0, lead).join('');
        if (reopen !== null) { const address = reopen; reopen = null; openLink(address); }
        if (link !== null && !link.hasText) link = { address: link.address, classes: spanClasses(inline.style), hasText: true };
        transition(inline.style);
        out += escape(chars.slice(lead, chars.length - trail).join(''));
        pendingSpace = ' '.repeat(trail);
        return;
      }
      case 'lineBreak': {
        const open: { address: string } | null = link;
        if (open !== null) { closeLink(plainStyle()); reopen = open.address; }
        transition(plainStyle());
        lines.push(out + '\\');
        out = '';
        return;
      }
      case 'footnoteReference':
        // A span can't hold a reference (its `[` would end the span's search for its close), nor can a link.
        closeLink(current);
        reopen = null;
        transition(emphasisOnly(current));
        out += `[^${inline.id}]`;
        return;
      case 'linkStart':
        closeLink(current);
        reopen = null;
        openLink(inline.address);
        return;
      case 'linkEnd':
        reopen = null;
        closeLink(styleAfter(index + 1));
        return;
    }
  });
  closeLink(plainStyle());
  transition(plainStyle());
  lines.push(out);
  return [lines[0], ...lines.slice(1).map(escapeLeading)].join('\n');
}

/** Escapes the characters the dialect reads as markup inside a paragraph (`*`, `\`, `[`, `]`). */
export function escape(text: string): string {
  let out = '';
  for (const c of graphemes(text)) {
    if (c === '*' || c === '\\' || c === '[' || c === ']') out += '\\';
    out += c;
  }
  return out;
}

/** Escapes a paragraph (or a line after a hard break) that would otherwise be read as a different block. */
export function escapeLeading(text: string): string {
  if (hasPrefix(text, '>') || parseListMarker(text) !== null) return '\\' + text;
  const chars = graphemes(text);
  let n = 0;
  while (n < chars.length && [...chars[n]].every((c) => isCFWhitespace(c.codePointAt(0)!))) n++;
  const indent = chars.slice(0, n).join('');
  const rest = text.slice(indent.length);
  if (hasPrefix(rest, ':::')) return indent + '\\' + rest;
  return text;
}

// MARK: Derived text

/** Plain text (pasted, dictated or captured) as prose: markers escaped, one paragraph per line. */
export function fromPlainText(text: string): string {
  return escape(text).replaceAll('\r\n', '\n').split('\n').map(trimCFWhitespace).filter((l) => l !== '')
    .map(escapeLeading).join('\n\n');
}

/** `plainText`: markup removed; blocks separated by blank lines. */
export function plainText(source: string): string {
  return parse(source).map((b) => (b.kind.type === 'sceneBreak' ? '* * *' : blockPlainText(b))).join('\n\n');
}

/** `plainParagraphs`: each block, and each hard-broken line within one, with blank ones left out. */
export function plainParagraphs(source: string): string[] {
  return plainText(source).split('\n').filter((l) => trimCFWhitespace(l) !== '');
}

/** `wordCount(plainText:)`: whitespace-separated tokens containing a letter or digit, over Unicode scalars. */
export function wordCountOfPlainText(text: string): number {
  let count = 0;
  let inWord = false;
  let hasContent = false;
  for (const cp of scalars(text)) {
    if (isCFWhitespaceOrNewline(cp)) {
      if (inWord && hasContent) count += 1;
      inWord = false;
      hasContent = false;
    } else {
      inWord = true;
      if (!hasContent && isCFAlphanumeric(cp)) hasContent = true;
    }
  }
  if (inWord && hasContent) count += 1;
  return count;
}

/** `wordCount(_:kind:)`: a screenplay counts its printed text (`Fountain.plainText`), everything else the dialect's. */
export function wordCount(source: string, kind: FragmentKind = 'prose'): number {
  return wordCountOfPlainText(kind === 'screenplay' ? fountainPlainText(source) : plainText(source));
}

/** `wordCount(_ blocks:)`: the count of already-parsed blocks. */
export function wordCountOfBlocks(blocks: Block[]): number {
  return blocks.reduce((n, b) => n + (b.kind.type === 'sceneBreak' ? 0 : wordCountOfPlainText(blockPlainText(b))), 0);
}

export const prose: Prose = {
  parse,
  plainText,
  plainParagraphs,
  wordCount,
  marker,
  startsList,
};
